import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDown, Check, ClipboardCopy, Download, X } from 'lucide-react';
import { useEffect, useState, type CSSProperties } from 'react';
import { cn } from '@/lib/cn';
import { useSession } from '@/session/store';
import { buildTrace, serializeTrace, traceFilename } from '@/session/trace';
import { Frames } from './Frames';
import { Turns } from './Turns';
import { useDrawerLayout } from './useDrawerLayout';

/** How far one arrow key moves the divider: visible, but fine enough to settle on a size. */
const NUDGE = 24;

const VIEWS = [
  { id: 'turns', label: 'Turns' },
  { id: 'frames', label: 'Frames' },
] as const;

type View = (typeof VIEWS)[number]['id'];

/** The log, and nothing else: the connection settings are a dialog of their own. */
export function DebugDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [view, setView] = useState<View>('turns');
  // Owned here rather than by the list, because the control that flips it sits in the section
  // header next to the one that chooses the list.
  const [newestFirst, setNewestFirst] = useState(true);
  const layout = useDrawerLayout();

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          // Overlays the conversation on a narrow window and sits beside it once there is room,
          // which is the whole point of a desktop-first layout that still works on a tablet.
          // `xl:relative` rather than `xl:static` so the drag edge stays anchored to this panel once
          // it is docked instead of to the page.
          className="dbg dbg-skin fixed inset-y-0 right-0 z-30 flex w-full flex-col border-l shadow-2xl xl:relative xl:inset-y-auto xl:right-auto xl:z-auto xl:shadow-none"
          style={{ '--dbg-width': `${layout.width}px` } as CSSProperties}
        >
          {/* A focusable separator is the ARIA splitter pattern: a widget, despite the plugin
              classing every `separator` as non-interactive. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Drag to resize the debug drawer"
            tabIndex={0}
            data-dragging={layout.dragging === 'width'}
            onPointerDown={layout.dragWidth}
            onKeyDown={(event) => {
              // Left widens: the drawer grows towards the pointer, not away from it.
              if (event.key === 'ArrowLeft') layout.nudgeWidth(NUDGE);
              else if (event.key === 'ArrowRight') layout.nudgeWidth(-NUDGE);
              else return;
              event.preventDefault();
            }}
            className="dbg-grip-x"
          />

          <header className="dbg-head">
            <h2 className="dbg-title">Debug</h2>
            <div className="flex items-center gap-1.5">
              <TraceExport />
              <button
                type="button"
                aria-label="Close the debug drawer"
                onClick={onClose}
                className="dbg-icon"
              >
                <X className="size-3.5" />
              </button>
            </div>
          </header>

          <Unhandled />

          <section className="dbg-detail">
            <header className="dbg-detail-head">
              <h3 className="dbg-detail-title">{view === 'turns' ? 'Turns' : 'Frames'}</h3>

              <div className="flex items-center gap-1.5">
                {VIEWS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={view === option.id}
                    onClick={() => setView(option.id)}
                    className="dbg-preset"
                  >
                    {option.label}
                  </button>
                ))}

                {view === 'turns' && (
                  <button
                    type="button"
                    aria-label={
                      newestFirst ? 'Show the oldest turn first' : 'Show the newest turn first'
                    }
                    onClick={() => setNewestFirst((value) => !value)}
                    className="dbg-icon"
                  >
                    <ArrowDown
                      className={cn('size-3.5 transition-transform', !newestFirst && 'rotate-180')}
                    />
                  </button>
                )}
              </div>
            </header>

            {view === 'turns' ? <Turns newestFirst={newestFirst} /> : <Frames />}
          </section>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

/**
 * What the server sent that this client dropped — the drawer's one warning, and the reason a turn
 * can be missing something the backend believes it delivered. Absent entirely when there is nothing
 * to report, which is the normal case.
 */
function Unhandled() {
  const frameTypes = useSession((state) => state.unknownFrameTypes);
  const payloadTypes = useSession((state) => state.unknownPayloadTypes);

  if (frameTypes.length === 0 && payloadTypes.length === 0) return null;

  return (
    <div className="dbg-unhandled">
      {payloadTypes.length > 0 && <Dropped label="payloads dropped" types={payloadTypes} />}
      {frameTypes.length > 0 && <Dropped label="frames dropped" types={frameTypes} />}
    </div>
  );
}

/** Types the renderer has no template for. */
function Dropped({ label, types }: { label: string; types: string[] }) {
  return (
    <div className="min-w-0">
      <span className="dbg-field block">
        <span>{label}</span>
      </span>
      <div className="mt-1 flex flex-wrap gap-1">
        {types.map((type) => (
          <span key={type} className="dbg-chip dbg-chip-dropped">
            {type}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Takes the whole session out of the browser: the same frames and timings the drawer shows, as one
 * file a tester can attach to a report, or on the clipboard for pasting into a ticket.
 */
function TraceExport() {
  const [copied, setCopied] = useState(false);

  // Read at click time rather than subscribed: a trace is a snapshot, and subscribing would
  // re-render this button on every frame of every turn.
  const snapshot = () => buildTrace(useSession.getState(), new Date());

  function save() {
    const trace = snapshot();
    const url = URL.createObjectURL(
      new Blob([serializeTrace(trace)], { type: 'application/json' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = traceFilename(new Date(trace.exportedAt));
    link.click();
    URL.revokeObjectURL(url);
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(serializeTrace(snapshot()));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (error) {
      // Denied clipboard permission is the usual cause; the download button still works.
      console.error('Could not copy the trace to the clipboard', error);
    }
  }

  return (
    <>
      <button
        type="button"
        aria-label="Copy the session trace to the clipboard"
        onClick={() => void copy()}
        className="dbg-icon"
      >
        {copied ? <Check className="size-3.5" /> : <ClipboardCopy className="size-3.5" />}
      </button>
      <button
        type="button"
        aria-label="Download the session trace as JSON"
        onClick={save}
        className="dbg-icon"
      >
        <Download className="size-3.5" />
      </button>
    </>
  );
}
